/**
 * QA Monitor - Memory and crash monitoring for debugging
 *
 * Usage: Import and call initQAMonitor() once on app startup
 */

import { debug } from '../lib/debug.js';

let initialized = false;
let memoryInterval = null;

/**
 * Initialize QA monitoring
 * - Tracks game start time
 * - Logs memory usage every 60 seconds
 * - Catches uncaught errors with context
 */
export function initQAMonitor() {
  if (initialized) return;
  initialized = true;

  // Track game start time
  window.gameStartTime = Date.now();
  window.activeFlightCount = 0;

  debug('[QA] Monitor initialized');

  // Memory monitoring - every 60 seconds
  memoryInterval = setInterval(() => {
    const mem = performance.memory;
    if (mem) {
      const heapMB = Math.round(mem.usedJSHeapSize / 1024 / 1024);
      const limitMB = Math.round(mem.jsHeapSizeLimit / 1024 / 1024);
      const audioCount = document.querySelectorAll('audio').length;
      const uptimeMins = Math.round((Date.now() - window.gameStartTime) / 1000 / 60);

      debug('[QA] Memory Check:', {
        heap: heapMB + 'MB',
        heapLimit: limitMB + 'MB',
        audio: audioCount,
        uptime: uptimeMins + ' mins',
        flights: window.activeFlightCount
      });

      // Warning threshold
      if (heapMB > 500) {
        console.error('[QA] MEMORY WARNING: Heap exceeds 500MB');
      }

      // Critical threshold
      if (heapMB > 800) {
        console.error('[QA] MEMORY CRITICAL: Heap exceeds 800MB - crash likely');
      }
    }
  }, 60000);

  // Crash detection - also saves to localStorage so it survives white screen
  window.onerror = (msg, url, line, col, error) => {
    const uptimeMins = Math.round((Date.now() - window.gameStartTime) / 1000 / 60);
    const heapMB = performance.memory
      ? Math.round(performance.memory.usedJSHeapSize / 1024 / 1024) + 'MB'
      : 'unknown';

    const crashData = {
      timestamp: new Date().toISOString(),
      message: msg,
      location: `${url}:${line}:${col}`,
      stack: error?.stack,
      uptime: uptimeMins + ' mins',
      activeFlights: window.activeFlightCount,
      heap: heapMB
    };

    console.error('[QA] CRASH:', crashData);

    // Save to localStorage so it survives white screen
    try {
      const crashes = JSON.parse(localStorage.getItem('qa_crashes') || '[]');
      crashes.push(crashData);
      // Keep last 10 crashes
      localStorage.setItem('qa_crashes', JSON.stringify(crashes.slice(-10)));
    } catch (e) {}
  };

  // Unhandled promise rejections
  window.onunhandledrejection = (event) => {
    const crashData = {
      timestamp: new Date().toISOString(),
      type: 'unhandledRejection',
      reason: String(event.reason),
      stack: event.reason?.stack,
      uptime: Math.round((Date.now() - window.gameStartTime) / 1000 / 60) + ' mins'
    };

    console.error('[QA] Unhandled Promise Rejection:', crashData);

    try {
      const crashes = JSON.parse(localStorage.getItem('qa_crashes') || '[]');
      crashes.push(crashData);
      localStorage.setItem('qa_crashes', JSON.stringify(crashes.slice(-10)));
    } catch (e) {}
  };
}

/**
 * Update active flight count (call from game loop)
 */
export function updateFlightCount(count) {
  window.activeFlightCount = count;
}

/**
 * Manual memory snapshot
 */
export function logMemorySnapshot(label = 'Manual') {
  const mem = performance.memory;
  if (mem) {
    debug(`[QA] Memory Snapshot (${label}):`, {
      heap: Math.round(mem.usedJSHeapSize / 1024 / 1024) + 'MB',
      audio: document.querySelectorAll('audio').length,
      uptime: Math.round((Date.now() - window.gameStartTime) / 1000 / 60) + ' mins'
    });
  }
}

/**
 * Cleanup (call on app unmount)
 */
export function cleanupQAMonitor() {
  if (memoryInterval) {
    clearInterval(memoryInterval);
    memoryInterval = null;
  }
  initialized = false;
}

/**
 * Get crash history from localStorage (survives white screen)
 * Call this after restart to see what crashed
 */
export function getCrashHistory() {
  try {
    return JSON.parse(localStorage.getItem('qa_crashes') || '[]');
  } catch (e) {
    return [];
  }
}

/**
 * Clear crash history
 */
export function clearCrashHistory() {
  localStorage.removeItem('qa_crashes');
}

// Expose to window for easy console access
if (typeof window !== 'undefined') {
  window.QA = {
    getCrashHistory,
    clearCrashHistory,
    logMemorySnapshot
  };
}

export default {
  initQAMonitor,
  updateFlightCount,
  logMemorySnapshot,
  cleanupQAMonitor,
  getCrashHistory,
  clearCrashHistory
};
